import { z } from "zod";
import { attributionSchema } from "./attribution";
import { emailSchema } from "./auth";
import { toStoredPhoneNumber } from "./phone";

/** "" → undefined so optional fields can be left blank in forms. */
const optionalText = (max: number, message?: string) =>
  z
    .string()
    .trim()
    .max(max, message ?? `Keep this under ${max} characters.`)
    .transform((value) => (value === "" ? undefined : value))
    .optional();

const nameField = (label: string) =>
  z
    .string()
    .trim()
    .min(1, `Enter your ${label}.`)
    .max(80, `Keep your ${label} under 80 characters.`);

/**
 * Required, but not checked against the country's numbering plan: anything
 * that looks like a full phone number is taken (see `toStoredPhoneNumber`).
 */
export const phoneField = z
  .string()
  .trim()
  .min(1, "Enter your phone number.")
  .max(30, "That phone number is too long.")
  .transform((value, ctx) => {
    const stored = toStoredPhoneNumber(value);
    if (!stored) {
      ctx.addIssue({ code: "custom", message: "That doesn't look like a full phone number." });
      return z.NEVER;
    }
    return stored;
  });

/**
 * Fields a person fills in (shared by the public form and manual "Add lead").
 * Only name, email and phone are required.
 */
export const leadFieldsSchema = z.object({
  firstName: nameField("first name"),
  lastName: nameField("last name"),
  email: emailSchema,
  phone: phoneField,
  company: optionalText(120),
  serviceId: z
    .union([z.literal(""), z.uuid("Choose one of the listed services.")])
    .transform((value) => (value === "" ? undefined : value))
    .optional(),
  message: optionalText(2000, "Keep your message under 2,000 characters."),
  marketingConsent: z.boolean().default(false),
});

export type LeadFieldsInput = z.input<typeof leadFieldsSchema>;
export type LeadFields = z.output<typeof leadFieldsSchema>;

/** Full public submission: fields + bot protection + attribution. Unknown keys are rejected. */
export const publicLeadSubmissionSchema = leadFieldsSchema
  .extend({
    turnstileToken: z.string().min(1, "Please complete the security check.").max(2048),
    /** Honeypot: hidden from people, filled in by naive bots. */
    website: z.string().max(200).optional(),
    /** When the form was first shown (ms since epoch), for a minimum fill time. */
    startedAt: z.number().int().positive().optional(),
    attribution: attributionSchema.optional(),
    /** Shared by the browser Pixel and the server's Conversions API event, for deduplication. */
    metaEventId: z.uuid().optional(),
    /** The page the form was submitted from (Meta's event_source_url). */
    pageUrl: z.string().trim().max(2048).optional(),
  })
  .strict();

export type PublicLeadSubmission = z.input<typeof publicLeadSubmissionSchema>;
